import { useCallback, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { GroupingModeEnum } from '@e-socna/data-layer';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { CheckboxList } from '../CheckboxList';
import { MemoBeforeSubmitForm } from '../../forms/MemoBeforeSubmit';
import { useMemos } from '../../forms/MemoBeforeSubmit/hooks/useMemos.hook';
import { useIsPricingEditable } from '../../contexts/PricingEditionContext/hooks/useIsPricingEditable.hook';
import type { GroupTotalCalculationMap } from '../../utils/build-group-total-calculation-map.util';

import { DEFAULT_SERVICE_USAGE_PLACEHOLDER_HEIGHT } from './constants/card.constant';
import { ServiceUsagesGroupBanner } from './GroupBanner';
import { ServiceUsagesTotalCard } from './TotalCard';
import { buildSelectedGroupsTotalCalculation } from './TotalCard/utils/selected-groups-total.util';
import {
  buildGlobalTotalOptionItems,
  withCheckedSections,
} from './utils/global-total-option-items.util';
import type { ServiceUsagesTotalCardCmp_TotalCalculation_Fragment } from './TotalCard';
import type { OptionGroupsSelectionControl } from './types/option-groups-selection-control.type';
import type {
  GlobalTotalOptionsGroup,
  GlobalTotalOptionsSection,
} from './utils/global-total-option-items.util';

const OPTIONS_WIDTH = 240;

export interface ServiceUsagesGlobalTotalOptions {
  readonly groupTotalCalculationMap: GroupTotalCalculationMap;
  readonly groups: readonly GlobalTotalOptionsGroup[];
  readonly initialSelectedOptionGroupIds?: readonly Id[] | null;
  readonly isSelectionDisabled?: boolean;
  readonly sections?: readonly GlobalTotalOptionsSection[];
  // Saves the choice on the server instead of keeping it in the page.
  readonly selectionControl?: OptionGroupsSelectionControl;
}

interface Props {
  readonly onStartCreateGroup?: () => void;
  readonly onStartReorder?: () => void;
  readonly onStartSwitchGroupingMode?: () => void;
  readonly options?: ServiceUsagesGlobalTotalOptions;
  readonly totalCalculation: FragmentType<
    typeof ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
  > | null;
}

// Only the unchecked options are kept in state, so that the groups created or
// made mandatory after the mount are counted in the total.
const getInitialUncheckedOptionGroupIds = (
  options: ServiceUsagesGlobalTotalOptions | undefined,
): readonly Id[] => {
  const selectedOptionGroupIds = options?.initialSelectedOptionGroupIds;

  if (!options || !selectedOptionGroupIds) {
    return [];
  }

  return options.groups
    .filter(
      (group) =>
        group.isOptional && !selectedOptionGroupIds.includes(group._id),
    )
    .map((group) => group._id);
};

const getCheckedGroupIds = (
  groups: readonly GlobalTotalOptionsGroup[],
  uncheckedOptionGroupIds: readonly Id[],
): readonly Id[] =>
  groups
    .filter(
      (group) =>
        !group.isOptional || !uncheckedOptionGroupIds.includes(group._id),
    )
    .map((group) => group._id);

const getUncheckedOptionGroupIds = (
  groups: readonly GlobalTotalOptionsGroup[],
  checkedGroupIds: readonly Id[],
): readonly Id[] =>
  groups
    .filter((group) => group.isOptional && !checkedGroupIds.includes(group._id))
    .map((group) => group._id);

// Only shown with several groups, hence always in Manual grouping mode.
export const ServiceUsagesGlobalTotal = ({
  onStartCreateGroup,
  onStartReorder,
  onStartSwitchGroupingMode,
  options,
  totalCalculation,
}: Readonly<Props>) => {
  const isPricingEditable = useIsPricingEditable();
  const { clearMemos, memos, setMemos } = useMemos();

  const selectionControl = options?.selectionControl;

  const [localUncheckedOptionGroupIds, setLocalUncheckedOptionGroupIds] =
    useState(() => getInitialUncheckedOptionGroupIds(options));
  // The choice waiting for its annotations before being saved.
  const [pendingUncheckedOptionGroupIds, setPendingUncheckedOptionGroupIds] =
    useState<readonly Id[] | null>(null);

  const groups = options?.groups;

  const uncheckedOptionGroupIds = selectionControl
    ? (pendingUncheckedOptionGroupIds ??
      getInitialUncheckedOptionGroupIds(options))
    : localUncheckedOptionGroupIds;

  const checkedGroupIds = useMemo(
    () => getCheckedGroupIds(groups ?? [], uncheckedOptionGroupIds),
    [groups, uncheckedOptionGroupIds],
  );

  const handleChange = useCallback(
    (nextCheckedGroupIds: readonly Id[]) => {
      const nextUncheckedOptionGroupIds = getUncheckedOptionGroupIds(
        groups ?? [],
        nextCheckedGroupIds,
      );

      if (!selectionControl) {
        setLocalUncheckedOptionGroupIds(nextUncheckedOptionGroupIds);

        return;
      }

      setPendingUncheckedOptionGroupIds(nextUncheckedOptionGroupIds);
      setMemos((oldMemos) => ({ ...oldMemos, isOpenMemoDialog: true }));
    },
    [groups, selectionControl, setMemos],
  );

  const clearPendingSelection = useCallback(() => {
    setPendingUncheckedOptionGroupIds(null);
    clearMemos();
  }, [clearMemos]);

  const saveSelection = useCallback(async () => {
    if (!selectionControl || !pendingUncheckedOptionGroupIds) {
      return;
    }

    await selectionControl.onChange(pendingUncheckedOptionGroupIds, memos);
    clearPendingSelection();
  }, [
    clearPendingSelection,
    memos,
    pendingUncheckedOptionGroupIds,
    selectionControl,
  ]);

  const hasOptions = !!options?.groups.some((group) => group.isOptional);

  const optionItems = useMemo(
    () => buildGlobalTotalOptionItems(groups ?? [], options?.sections ?? []),
    [groups, options?.sections],
  );

  const displayedTotalCalculation = useMemo(
    () =>
      totalCalculation && options && hasOptions
        ? buildSelectedGroupsTotalCalculation(
            options.groupTotalCalculationMap,
            checkedGroupIds,
          )
        : totalCalculation,
    [checkedGroupIds, hasOptions, options, totalCalculation],
  );

  return (
    <Box component="section" sx={{ mb: 3 }}>
      {selectionControl ? (
        <MemoBeforeSubmitForm
          clearMemos={clearMemos}
          isLoading={selectionControl.isLoading}
          memos={memos}
          onCancel={clearPendingSelection}
          onConfirm={saveSelection}
          setMemos={setMemos}
          submitLabel="Enregistrer les options"
        />
      ) : null}
      <ServiceUsagesGroupBanner
        editionMode={false}
        groupingMode={GroupingModeEnum.Manual}
        label="Total"
        onStartCreateGroup={onStartCreateGroup}
        onStartReorder={onStartReorder}
        onStartSwitchGroupingMode={onStartSwitchGroupingMode}
      />
      <ServiceUsagesTotalCard
        aside={
          options && hasOptions ? (
            <Box
              sx={{
                alignSelf: 'center',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: DEFAULT_SERVICE_USAGE_PLACEHOLDER_HEIGHT,
                flexShrink: 0,
                width: { md: OPTIONS_WIDTH },
                p: 2,
              }}
            >
              <Typography gutterBottom variant="subtitle2">
                Groupes inclus
              </Typography>
              <Box
                sx={{
                  minHeight: 0,
                  overflowY: 'auto',
                  '& .MuiFormControlLabel-label': { overflowWrap: 'anywhere' },
                }}
              >
                <CheckboxList<Id>
                  disabled={
                    selectionControl
                      ? selectionControl.isDisabled ||
                        selectionControl.isLoading
                      : (options.isSelectionDisabled ?? !isPricingEditable)
                  }
                  idPrefix="global-total-groups-"
                  items={optionItems}
                  onChange={handleChange}
                  selectAllLabel="Toutes les options"
                  value={withCheckedSections(optionItems, checkedGroupIds)}
                  variant="edition"
                />
              </Box>
            </Box>
          ) : undefined
        }
        title="Total global"
        totalCalculation={displayedTotalCalculation}
      />
    </Box>
  );
};
